<template>
  <MosaicListView
    class="articles-mosaic-list"
    :items="articles"
    :more-button="hasMoreArticles"
    :more-button-text="t('showMore')"
    more-data-test="destination-articles-btn-read-more"
    :loading="loading"
    no-last-item-margin
    :number-of-skeleton-cards="2"
    @load-more="fetchData"
  >
    <template #default="{ item, mode }">
      <!-- TODO: заменить на AppLink и использование директивы клика после полного переезда на МФ -->
      <a
        class="article-card-wrapper"
        :href="`/${item.type}s/${item.slug}/`"
        @click.prevent="goToArticle(item)"
      >
        <Card
          :item="item"
          :mode="mode"
          :locale="dateRu"
        />
      </a>
    </template>
    <template #skeleton="{ mode }">
      <Skeleton
        :mode="mode"
        :loading="loading"
      />
    </template>
  </MosaicListView>
</template>

<script lang="ts" setup>
  import { useAppRouter } from '@experience/core-app-link-lib';
  import { useI18n } from '@experience/i18n';
  import { type JournalContentItem, Card, Skeleton } from '@experience/mosaic-list-article-card-package';
  import { MosaicListView } from '@experience/mosaic-list-view-package';
  import { ru as dateRu } from 'date-fns/locale';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  import { trackEvent } from '@/services/analytics';
  import { ru } from '@/pages/cityArticles/features/journalContentList/i18n/ru';
  import { useJournalContent } from '@/entities/journalContent';
  import { tryAwait } from '@/shared/utils/tryAwait';

  interface Props {
    /**
     * Slug города.
     */
    citySlug: string;

    /**
     * Размер страницы.
     */
    pageSize: number;

    /**
     * Идентификатор города.
     */
    subjectGeoId: number;

    /**
     * Название города.
     */
    subjectGeoName: string;

    /**
     * Флаг загрузки сторонних данных.
     *
     * Данный флаг нужен для того, чтобы не отсылать события аналитики до того
     * как загрузятся все данные, отправляемые в событиях.
     */
    generalPageDataLoading: boolean;
  }

  const props = defineProps<Props>();

  const { routerProxy } = useAppRouter();
  const { t } = useI18n({ inheritLocale: true, messages: { ru } });

  const store = useJournalContent();
  const { getArticles } = store;
  const { articles, loading: articlesLoading, hasNext: hasMoreArticles } = storeToRefs(store);
  const loading = computed(() => articlesLoading.value || props.generalPageDataLoading);

  const goToArticle = async (item: JournalContentItem) => {
    await tryAwait(() =>
      trackEvent('city-articles-info:section:snippet.click', {
        subject_geo_id: props.subjectGeoId,
        subject_geo_name: props.subjectGeoName,
        subject_geo_type: 'city',
        section_type: 'article',
        snippet_id: item.id,
      }),
    );

    await routerProxy.push(`/${item.type}s/${item.slug}/`);
  };

  const fetchData = async () => {
    await tryAwait(() =>
      trackEvent('city-articles-info:section:show-all.click', {
        subject_geo_id: props.subjectGeoId,
        subject_geo_name: props.subjectGeoName,
        subject_geo_type: 'city',
        section_type: 'article',
      }),
    );

    await getArticles({ slugType: 'city', slug: props.citySlug, pageSize: props.pageSize });
  };
</script>

<style lang="scss" scoped>
  .articles-mosaic-list {
    margin-top: var(--alias-space-32);

    @include tablet {
      margin-top: var(--alias-space-16);
    }
  }

  .article-card-wrapper {
    color: var(--alias-color-text-default);
    text-decoration: none;

    &:focus,
    &:visited {
      color: var(--alias-color-text-default);
      text-decoration: none;
    }
  }
</style>
